import { Slot } from '@radix-ui/react-slot';
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: 'primary' | 'secondary' | 'quiet' | 'danger' | 'buy' | 'sell';
  readonly size?: 'sm' | 'md' | 'lg';
  readonly asChild?: boolean;
}

export function Button({ variant = 'secondary', size = 'md', asChild = false, className, ...props }: ButtonProps) {
  const Component = asChild ? Slot : 'button';
  return <Component className={cx('ui-button', `ui-button--${variant}`, `ui-button--${size}`, className)} {...props} />;
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly label: string;
  readonly children: ReactNode;
}

export function IconButton({ label, children, className, ...props }: IconButtonProps) {
  return <button type="button" aria-label={label} title={label} className={cx('ui-icon-button', className)} {...props}>{children}</button>;
}

export interface PanelProps extends HTMLAttributes<HTMLElement> {
  readonly title?: string;
  readonly eyebrow?: string;
  readonly tools?: ReactNode;
  readonly as?: 'section' | 'aside' | 'div';
}

export function Panel({ title, eyebrow, tools, as: Component = 'section', className, children, ...props }: PanelProps) {
  return (
    <Component className={cx('ui-panel', className)} {...props}>
      {(title || eyebrow || tools) && (
        <header className="ui-panel__header">
          <div>{eyebrow && <span className="ui-panel__eyebrow">{eyebrow}</span>}{title && <h2>{title}</h2>}</div>
          {tools && <div className="ui-panel__tools">{tools}</div>}
        </header>
      )}
      {children}
    </Component>
  );
}

export interface StatusPillProps extends HTMLAttributes<HTMLSpanElement> {
  readonly tone?: 'neutral' | 'positive' | 'warning' | 'negative' | 'info';
}

export function StatusPill({ tone = 'neutral', className, children, ...props }: StatusPillProps) {
  return <span className={cx('ui-status-pill', `ui-status-pill--${tone}`, className)} {...props}>{children}</span>;
}
